import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { FiPieChart } from 'react-icons/fi';

const PlatformBreakdownChart = ({ platformDoughnutData, analyticsData, isLoading }) => {
  const total = (analyticsData?.webUsers || 0) + (analyticsData?.appIosUsers || 0);
  const webPct = Math.round(((analyticsData?.webUsers || 0) / total) * 100);
  const iosPct = Math.round(((analyticsData?.appIosUsers || 0) / total) * 100);

  const [tooltip, setTooltip] = React.useState({
    visible: false,
    x: 0,
    y: 0,
    label: '',
    value: '',
    percentage: '',
    color: '',
    xAlign: 'center',
  });

  const options = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
      tooltip: {
        enabled: false, // Use custom HTML tooltip instead
        external: (context) => {
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
            return;
          }

          const item = tooltipModel.dataPoints?.[0];
          if (!item) return;

          const rawValue = item.raw || 0;
          const pct = total > 0 ? Math.round((rawValue / total) * 100) : 0;
          const color = item.dataset.backgroundColor?.[item.dataIndex] || '#CC9900';

          setTooltip({
            visible: true,
            x: tooltipModel.caretX,
            y: tooltipModel.caretY - 8,
            label: item.label || '',
            value: rawValue.toLocaleString(),
            percentage: `${pct}%`,
            color,
            xAlign: tooltipModel.xAlign || 'center',
          });
        }
      }
    },
    cutout: '72%',
  }), [total]);

  const transformStyle = React.useMemo(() => {
    if (tooltip.xAlign === 'left') return 'translate(-15%, -100%)';
    if (tooltip.xAlign === 'right') return 'translate(-85%, -100%)';
    return 'translate(-50%, -100%)';
  }, [tooltip.xAlign]);

  const caretLeftStyle = React.useMemo(() => {
    if (tooltip.xAlign === 'left') return '15%';
    if (tooltip.xAlign === 'right') return '85%';
    return '50%';
  }, [tooltip.xAlign]);

  return (
    <div className="bg-white border border-gray-100 rounded-[24px] p-6 shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)] hover:shadow-[0px_12px_24px_-4px_rgba(16,24,40,0.08),0px_4px_8px_-2px_rgba(16,24,40,0.03)] transition-all duration-500 ease-in-out flex flex-col justify-between h-full">
      <div className="pb-4 border-b border-gray-100">
        <h3 className="text-lg font-bold text-[#111827] flex items-center gap-2">
          <FiPieChart className="text-[#CC9900]" /> Platform Breakdown
        </h3>
        <p className="text-xs text-gray-400 mt-0.5">Web vs App user distribution.</p>
      </div>

      <div className="h-[220px] w-full my-4 flex items-center justify-center relative">
        {isLoading || !analyticsData ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-[3px] border-gray-100 border-t-[#CC9900] rounded-full animate-spin" />
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest animate-pulse">Collecting Report...</p>
          </div>
        ) : (
          <>
            <Doughnut
              data={platformDoughnutData}
              options={options}
            />
            
            {/* Total Center Label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-8">
              <span className="text-xs font-semibold text-gray-400">Total Users</span>
              <span className="text-xl font-extrabold text-[#111827]">
                {analyticsData?.totalUsers?.toLocaleString()}
              </span>
            </div>
          </>
        )}

        {/* Custom Tooltip */}
        {tooltip.visible && (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x,
              top: tooltip.y,
              transform: transformStyle,
              pointerEvents: 'none',
              zIndex: 50,
              background: 'rgba(15, 23, 42, 0.98)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '12px',
              padding: '10px 14px',
              color: '#F8FAFC',
              fontSize: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 10px rgba(0, 0, 0, 0.2)',
              minWidth: '150px',
              fontFamily: "'Outfit', 'Inter', sans-serif"
            }}
          >
            {/* Header / Platform Name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: tooltip.color }} />
              <span style={{ fontWeight: 700, color: '#F8FAFC' }}>{tooltip.label}</span>
            </div>

            {/* Metrics */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#94A3B8', fontWeight: 500 }}>Users</span>
                <span style={{ fontWeight: 600, color: '#F8FAFC' }}>{tooltip.value}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#94A3B8', fontWeight: 500 }}>Share</span>
                <span style={{ fontWeight: 700, color: '#CC9900' }}>{tooltip.percentage}</span>
              </div>
            </div>

            {/* Arrow caret */}
            <div style={{
              position: 'absolute',
              bottom: '-6px',
              left: caretLeftStyle,
              transform: 'translateX(-50%) rotate(45deg)',
              width: '10px',
              height: '10px',
              background: 'rgba(15, 23, 42, 0.98)',
              borderRight: '1px solid rgba(255, 255, 255, 0.15)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
              pointerEvents: 'none',
            }} />
          </div>
        )}
      </div>

      <div className="space-y-2 pt-2 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-2 text-gray-600">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Guest User
          </span>
          <span className="font-bold text-gray-900">{analyticsData?.webUsers?.toLocaleString()} ({webPct}%)</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-2 text-gray-600">
            <span className="w-2.5 h-2.5 rounded-full bg-[#CC9900]" /> App
          </span>
          <span className="font-bold text-gray-900">{analyticsData?.appIosUsers?.toLocaleString()} ({iosPct}%)</span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PlatformBreakdownChart);
